import CustomSnackBar from "@components/CustomSnackBar";
import UserForm from "@components/UserForm";
import { ROUTING_ADMIN_USERS } from "@constants/adminPanelRouting";
import {
  INITIAL_SNACKBAR_STATE,
  ISnackbarAlert,
  setErrorAlert,
  setInitialSnackbarState
} from "@constants/alerts";
import ROUTES from "@constants/routing";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  SelectChangeEvent,
  Typography
} from "@mui/material";
import { useSaveUser } from "@services/api";
import { T_BOOL_FIELD, UserCreate } from "@services/api/types";
import { useQueryClient } from "@tanstack/react-query";
import { getTranslationKeyFromAPIError } from "@utils/api_errors";
import NavigationButtons from "@views/ViewAdminPanel/components/NavigationButtons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

type T_USER_STATUS = "draft" | "saving" | "created" | "failed";

const ViewNewUser = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [userStatus, setUserStatus] = useState<T_USER_STATUS>("draft");
  const [user, setUser] = useState<UserCreate>({
    email: "",
    username: "",
    first_name: "",
    last_name: "",
    enabled: true,
    is_email_verified: false,
    password: "",
    is_password_temporary: true,
    is_admin: false,
    clients: [],
    locale: "en"
  });
  const [snackbarAlert, setSnackbarAlert] = useState<ISnackbarAlert>({
    ...INITIAL_SNACKBAR_STATE
  });
  const [userSaving, setUserSaving] = useState(false);

  const queryClient = useQueryClient();
  const mutationSaveUser = useSaveUser();

  const onGoBack = () => {
    navigate(ROUTES.ADMIN_PANEL + ROUTING_ADMIN_USERS);
  };

  const onContinue = () => {
    setUserStatus("draft");
    navigate(ROUTES.ADMIN_PANEL + ROUTING_ADMIN_USERS);
  };

  const handleFieldChange = (field: keyof UserCreate, value: any) => {
    const value_fmt = `${value}`.length > 0 ? value : "";
    const updated = { ...user, [field]: value_fmt };
    setUser(updated);
  };

  const handleBoolFieldChanged = (field: T_BOOL_FIELD, value: boolean) => {
    const updated = { ...user, [field]: value };
    if (field === "is_admin") {
      updated.clients = [];
    }
    setUser(updated);
  };

  const handleClientsChange = (event: SelectChangeEvent) => {
    const {
      target: { value }
    } = event;

    const selectedClients =
      typeof value === "string"
        ? (value.split(",") as string[])
        : (value as string[]);
    const selectedClientsIds = selectedClients.map((client) =>
      parseInt(client)
    );

    const updated = {
      ...user,
      clients: selectedClientsIds
    };
    setUser(updated);
  };

  const onSave = (e: any) => {
    e.preventDefault();
    if (!e.target.checkValidity()) {
      setSnackbarAlert(setErrorAlert(t("newUser.validation.formInvalid")));
    } else {
      setUserStatus("saving");
      setUserSaving(true);
      mutationSaveUser.mutate(user, {
        onSuccess: () => {
          setUserStatus("created");
          setUserSaving(false);
          queryClient.invalidateQueries({
            queryKey: ["users"]
          });
          queryClient.refetchQueries({
            queryKey: ["users"]
          });
        },
        onError: (error: any) => {
          setUserStatus("draft");
          setUserSaving(false);
          const translationKey = getTranslationKeyFromAPIError(
            "newUser.errors",
            error
          );
          const messageKey = i18n.exists(translationKey)
            ? translationKey
            : "newUser.errorSaving";
          setSnackbarAlert(
            setErrorAlert(t(messageKey, { name: user.username }))
          );
        }
      });
    }
  };

  return (
    <Box className="w-full p-4">
      <NavigationButtons
        disabled={userStatus === "saving"}
        onGoBack={onGoBack}
      />
      <Box className="flex justify-center w-full p-2 md:p-8 mt-8">
        <Paper className="w-full">
          <Box className="flex items-center justify-center pt-16 gap-2">
            <PersonAddIcon color="success" sx={{ fontSize: "2.0rem" }} />
            <Typography variant="h6">{t("common.newUser")}</Typography>
          </Box>
          {userStatus === "draft" && (
            <UserForm
              mode="create"
              user={user}
              onSave={onSave}
              onGoBack={onGoBack}
              processing={userSaving}
              handleFieldChange={handleFieldChange}
              handleBoolFieldChanged={handleBoolFieldChanged}
              handleClientsChange={handleClientsChange}
            />
          )}

          {userStatus === "saving" && (
            <Box className="w-full p-8">
              <Typography variant="h5" color="success" className="pb-4">
                {t("createUserProgressDialog.status.userSaving")}
              </Typography>
              <LinearProgress color="success" sx={{ height: "1.0rem" }} />
            </Box>
          )}
          {userStatus === "created" && (
            <Box className="w-full p-8">
              <Typography variant="h5" color="success" className="pb-4">
                {t("createUserProgressDialog.status.userCreated")}
              </Typography>

              <Button color="success" variant="contained" onClick={onContinue}>
                {t("common.continue")}
                <ArrowForwardIosIcon sx={{ fontSize: "1.5rem" }} />
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
      <CustomSnackBar
        id={snackbarAlert.message}
        key={snackbarAlert.message}
        open={snackbarAlert.open}
        message={snackbarAlert.message}
        handleClose={() => setSnackbarAlert(setInitialSnackbarState())}
        autoHideDuration={snackbarAlert.autoHideDuration}
        color={snackbarAlert.severity}
      />
    </Box>
  );
};

export default ViewNewUser;
